import cx from 'classnames'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import ImageStyles from 'common/Prose/nodeViews/Image/Image.module.scss'
import { buildImageDragClone } from 'common/Prose/nodeViews/Image/utils'
import LineDividerStyles from 'common/Prose/nodeViews/LineDivider/LineDivider.module.scss'
import { observer } from 'mobx-react-lite'
import { schema } from 'prose-core'
import { NodeSelection } from 'prosemirror-state'
import React, { useRef } from 'react'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { AlmanacDropCursorPluginMeta, AlmanacDropCursorPluginState } from '../../AlmanacDropCursor/AlmanacDropCursor'
import { ALMANAC_DROP_CURSOR_PLUGIN_KEY } from '../../AlmanacDropCursor/constants'
import { NodeHandlesPluginKey, NodeHandlesPluginState } from '../NodeHandles'
import { ReactComponent as DragDotsIcon } from '../images/dragDots.svg'
import styles from './DragHandle.module.scss'

export const DragHandle: React.FC<React.PropsWithChildren<{ getPos: () => number | undefined }>> = observer(
  ({ getPos }) => {
    const editorView = useEditorView()
    const nodePos = getPos()
    const node = nodePos == null ? null : editorView.state.doc.resolve(nodePos).nodeAfter
    const dropCursorPluginState: AlmanacDropCursorPluginState = ALMANAC_DROP_CURSOR_PLUGIN_KEY.getState(
      editorView.state,
    )
    const selfRef = useRef<HTMLDivElement>(null)

    if (node == null || nodePos == null) return null

    const handleDragStart = (e: React.MouseEvent) => {
      const {
        state: { tr },
        dispatch,
      } = editorView
      const meta: AlmanacDropCursorPluginMeta = { action: 'startDrag', draggedNodePos: nodePos }
      if (selfRef.current) {
        switch (node.type) {
          case schema.nodes.image:
            {
              const clone = buildImageDragClone(
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                selfRef.current.parentElement!.nextElementSibling!.querySelector(
                  `div.${ImageStyles.brokenImage}, img.${ImageStyles.mainImage}`,
                ) as any,
              )
              meta.targetElementClone = clone
            }
            break
          case schema.nodes.table_of_contents:
          case schema.nodes.iframe:
          case schema.nodes.download:
          case schema.nodes.card:
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const elementToClone = selfRef.current!.parentElement!.nextElementSibling! as HTMLElement
            const clone = elementToClone.cloneNode(true) as HTMLElement
            clone.id = 'dragClone'
            if (node.type === schema.nodes.iframe) {
              clone.style.setProperty('max-width', `${elementToClone.offsetWidth}px`)
            }
            meta.targetElementClone = clone
            break
          case schema.nodes.line_divider:
            {
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const clone = selfRef
                .current!.parentElement!.nextElementSibling!.querySelector(`.${LineDividerStyles.wrapper}`)!
                .cloneNode(true) as HTMLElement
              clone.id = 'dragClone'
              meta.targetElementClone = clone
            }
            break
        }
      }
      dispatch(tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta))
      trackEventInHeap(HeapEventType.SideStacking, { action: 'start-drag', type: node?.type?.name })
    }

    const handleSelectNode = () => {
      const {
        state: { tr, doc },
        dispatch,
      } = editorView
      const { hoveredNode } = NodeHandlesPluginKey.getState(editorView.state) as NodeHandlesPluginState
      if (hoveredNode === null) return

      dispatch(tr.setSelection(NodeSelection.create(doc, hoveredNode.pos)))
      editorView.focus()
      trackEventInHeap(HeapEventType.SideStacking, { action: 'select-node', type: node?.type?.name })
    }

    return (
      <div
        className={cx(styles.NodeDragHandle, { [styles.isDragging]: dropCursorPluginState?.isDragging })}
        onMouseDown={(e) => {
          handleDragStart(e)
          setTimeout(() => {
            handleSelectNode()
          }, 0)
        }}
        ref={selfRef}
      >
        <DragDotsIcon />
      </div>
    )
  },
)
